import { atom } from "jotai"

import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  StudentConstructor,
  StudentSkillType,
} from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { getAllSkillLevelsAsOf } from "#utils/studentSkillHelpers"

import { createDocAtoms } from "./createDocAtoms"

/**
 * STUDENT ATOMS
 * Using the generic factory to create atoms for student documents
 */
export const studentAtoms = createDocAtoms<StudentConstructor>(
  DOC_TYPES.STUDENTS
)

/**
 * DERIVED ATOMS: Student-specific computed data
 * These are examples of domain-specific slices/selectors
 */

/**
 * Get students by class ID (from attendance records)
 * This is a derived atom that filters students based on class enrollment
 */
export const studentsByClassIdAtom = atom((get) => {
  const allStudents = get(studentAtoms.allDocsAtom)

  return (classId: Uuid): StudentConstructor[] => {
    const filtered = allStudents.filter((student) => {
      return student.attendance != null && student.attendance[classId] != null
    })

    // console.log(
    //   `${studentAtoms.logPrefix} 🎓 Computed students for classId ${classId}: ${filtered.length} students`
    // )
    return filtered
  }
})

/**
 * Get student display names
 * Pre-computed display data that can be reused across components
 */
export const studentDisplayNamesAtom = atom((get) => {
  const studentsMap = get(studentAtoms.docsMapAtom)
  const displayMap = new Map<Uuid, string>()

  studentsMap.forEach((student, id) => {
    displayMap.set(id, `${student.firstName} ${student.lastName}`)
  })

  // console.log(
  //   `${studentAtoms.logPrefix} 📛 Computed display names, count: ${displayMap.size}`
  // )
  return displayMap
})

/**
 * Count of students by skill level
 * Example of aggregated computed state
 */
export const studentSkillLevelCountsAtom = atom((get) => {
  const allStudents = get(studentAtoms.allDocsAtom)

  const counts = {
    core: {} as Record<string, number>,
    digital: {} as Record<string, number>,
    sculpture: {} as Record<string, number>,
  }

  allStudents.forEach((student) => {
    const levels = getAllSkillLevelsAsOf(student)

    for (const skillType of Object.values(StudentSkillType)) {
      const level = levels[skillType]
      if (!level) continue
      counts[skillType][level] = (counts[skillType][level] || 0) + 1
    }
  })

  // console.log(
  //   `${studentAtoms.logPrefix} 📊 Computed skill level counts`,
  //   counts
  // )
  return counts
})
